import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum ElementVisibility {
  ALL = "all",
  DESKTOP_ONLY = "desktop-only",
  MOBILE_ONLY = "mobile-only",
}

const baseElementSchema = z.object({
  _id: z.string().optional(),
  visibility: createEnum(ElementVisibility, {
    enumName: "ElementVisibility",
  }).optional(),
  backgroundColor: z.string().optional(),
  borderColor: z.string().optional(),
  borderWidth: z.number().optional(),
  cornerRadius: z.number().optional(),
  padding: z
    .object({
      top: z.number(),
      right: z.number(),
      bottom: z.number(),
      left: z.number(),
    })
    .optional(),
});
export interface BaseElement extends z.infer<typeof baseElementSchema> {}

export enum MarketingForgeElementType {
  RICH_TEXT = "rich-text",
  BUTTON = "button",
  INPUT = "input",
  IMAGE = "image",
  PRODUCT = "product",
  COLUMN = "column",
  CONTAINER = "container",
  DISCOUNT = "discount",
  TEXT_TO_SUBSCRIBE_BUTTON = "text-to-subscribe-button",
  TEXT_TO_SUBSCRIBE_DESCRIPTION = "text-to-subscribe-description",
}

export enum ButtonAction {
  SUBMIT_AND_ADVANCE = "submit-and-advance",
  SUBMIT_AND_GO_TO_URL = "submit-and-go-to-url",
  CLOSE = "close",
}

// Button interaction types for Spotlights
export enum ButtonInteractionType {
  GO_TO_NEXT = "go-to-next",
  GO_TO_STEP = "go-to-step",
  CLOSE = "close",
  CLOSE_AND_ACTION = "close-and-action",
  OPEN_LINK = "open-link",
}

// Close action types (for close-and-action interaction)
export enum CloseActionType {
  ADD_DISCOUNT_TO_CART = "add-discount-to-cart",
  OPEN_CART = "open-cart",
  OPEN_CHECKOUT = "open-checkout",
  ADD_PRODUCT_TO_CART = "add-product-to-cart",
  CLICK_ELEMENT = "click-element",
}

/** Returns true for action types that may navigate the page away. */
export function closeActionMayNavigate(type: CloseActionType): boolean {
  return (
    type === CloseActionType.OPEN_CART ||
    type === CloseActionType.OPEN_CHECKOUT ||
    type === CloseActionType.CLICK_ELEMENT
  );
}

// Close action schemas
const addDiscountToCartActionSchema_ = z.object({
  type: z.literal(CloseActionType.ADD_DISCOUNT_TO_CART),
  discountId: z.string(),
});
export interface AddDiscountToCartAction extends z.infer<
  typeof addDiscountToCartActionSchema_
> {}

const openCartActionSchema_ = z.object({
  type: z.literal(CloseActionType.OPEN_CART),
});
export interface OpenCartAction extends z.infer<typeof openCartActionSchema_> {}

const openCheckoutActionSchema_ = z.object({
  type: z.literal(CloseActionType.OPEN_CHECKOUT),
});
export interface OpenCheckoutAction extends z.infer<
  typeof openCheckoutActionSchema_
> {}

const addProductToCartActionSchema_ = z.object({
  type: z.literal(CloseActionType.ADD_PRODUCT_TO_CART),
  productId: z.string().optional(),
  variantId: z.string().optional(),
  quantity: z.number().optional(),
  productTitle: z.string().optional(),
  productImageUrl: z.string().optional(),
});
export interface AddProductToCartAction extends z.infer<
  typeof addProductToCartActionSchema_
> {}

// Clicks a storefront DOM element matched by `selector` (e.g. the merchant's
// own add-to-cart button). Lets the merchant's existing storefront behavior
// (bundles, selling plans, redirect-to-checkout) run unchanged instead of
// re-implementing it in a typed action.
const clickElementActionSchema_ = z.object({
  type: z.literal(CloseActionType.CLICK_ELEMENT),
  selector: z.string(),
});
export interface ClickElementAction extends z.infer<
  typeof clickElementActionSchema_
> {}

const closeActionSchema_ = z.discriminatedUnion("type", [
  addDiscountToCartActionSchema_,
  openCartActionSchema_,
  openCheckoutActionSchema_,
  addProductToCartActionSchema_,
  clickElementActionSchema_,
]);
export const CloseActionSchema: z.ZodType<CloseAction> = closeActionSchema_;
export type CloseAction = z.infer<typeof closeActionSchema_>;

// Button interaction schemas
const goToNextInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.GO_TO_NEXT),
});
export interface GoToNextInteraction extends z.infer<
  typeof goToNextInteractionSchema_
> {}

const goToStepInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.GO_TO_STEP),
  stepId: z.string(),
});
export interface GoToStepInteraction extends z.infer<
  typeof goToStepInteractionSchema_
> {}

const closeInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.CLOSE),
});
export interface CloseInteraction extends z.infer<
  typeof closeInteractionSchema_
> {}

const closeAndActionInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.CLOSE_AND_ACTION),
  actions: z.array(closeActionSchema_),
});
export interface CloseAndActionInteraction extends z.infer<
  typeof closeAndActionInteractionSchema_
> {}

const openLinkInteractionSchema_ = z.object({
  interactionType: z.literal(ButtonInteractionType.OPEN_LINK),
  url: z.string(),
  openInNewTab: z.boolean(),
});
export interface OpenLinkInteraction extends z.infer<
  typeof openLinkInteractionSchema_
> {}

const buttonInteractionSchema_ = z.discriminatedUnion("interactionType", [
  goToNextInteractionSchema_,
  goToStepInteractionSchema_,
  closeInteractionSchema_,
  closeAndActionInteractionSchema_,
  openLinkInteractionSchema_,
]);
export const ButtonInteractionSchema: z.ZodType<ButtonInteraction> =
  buttonInteractionSchema_;
export type ButtonInteraction = z.infer<typeof buttonInteractionSchema_>;

export enum CustomFieldType {
  Date = "date",
  Dropdown = "dropdown",
  Number = "number",
  TextLong = "text-long",
  TextShort = "text-short",
  YesNoQuestion = "yes-no-question",
}

export const fontWeights = ["regular", "semibold", "bold"] as const;
export type FontWeight = (typeof fontWeights)[number];
export const FontWeight = fontWeights;

export const buttonHierarchies = ["primary", "secondary", "tertiary"] as const;
export type ButtonHierarchy = (typeof buttonHierarchies)[number];
export const ButtonHierarchy = buttonHierarchies;

const elementStyleOverrideSchema_ = z.object({
  backgroundColor: z.string(),
  textColor: z.string(),
  border: z.object({
    cornerRadiusPx: z.number(),
    stroke: z.object({ color: z.string(), weightPx: z.number() }),
  }),
  padding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  font: z.object({
    fontFamily: z.string(),
    fontSizePx: z.number(),
    fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  }),
});

const buttonStyleOverrideSchema_ = elementStyleOverrideSchema_;
export const ButtonStyleOverrideSchema: z.ZodType<ButtonStyleOverride> =
  buttonStyleOverrideSchema_;
export interface ButtonStyleOverride extends z.infer<
  typeof buttonStyleOverrideSchema_
> {}

const inputStyleOverrideSchema_ = elementStyleOverrideSchema_.extend({
  errorColor: z.string(),
});
export const InputStyleOverrideSchema: z.ZodType<InputStyleOverride> =
  inputStyleOverrideSchema_;
export interface InputStyleOverride extends z.infer<
  typeof inputStyleOverrideSchema_
> {}

export const inputTypes = [
  "email",
  "phone",
  "birthday",
  "firstName",
  "lastName",
  "fullName",
  "custom",
] as const;
export type InputType = (typeof inputTypes)[number];
export const InputType = inputTypes;

export enum FontFamily {
  ARIAL = "Arial",
  COURIER_NEW = "Courier New",
  GEORGIA = "Georgia",
  LUCIDA_SANS_UNICODE = "Lucida Sans Unicode",
  TAHOMA = "Tahoma",
  TIMES_NEW_ROMAN = "Times New Roman",
  TREBUCHET_MS = "Trebuchet MS",
  VERDANA = "Verdana",
  NUNITO = "Nunito",
  POPPINS = "Poppins",
  RALEWAY = "Raleway",
  INTER = "Inter",
  PACIFICO = "Pacifico",
}

const richTextElementSchema_ = baseElementSchema.extend({
  /** @deprecated use the baseElementSchema._id instead */
  id: z.union([z.number(), z.string()]),
  type: z.literal(MarketingForgeElementType.RICH_TEXT),
  html: z.string(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSizePx: z.number(),
  textColor: z.string(),
  linkColor: z.string(),
});
export const richTextElementSchema: z.ZodType<RichTextElement> =
  richTextElementSchema_;
export const RichTextElementSchema: z.ZodType<RichTextElement> =
  richTextElementSchema_;
export interface RichTextElement extends z.infer<
  typeof richTextElementSchema_
> {}

const buttonElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.BUTTON),
  hierarchy: createEnum(buttonHierarchies, { enumName: "ButtonHierarchy" }),
  text: z.string(),
  action: createEnum(ButtonAction, { enumName: "ButtonAction" }),
  destinationUrl: z.string().optional(),
  interaction: buttonInteractionSchema_.optional(),
  styleOverride: buttonStyleOverrideSchema_.nullish(),
});
export const ButtonElementSchema: z.ZodType<ButtonElement> =
  buttonElementSchema;
export interface ButtonElement extends z.infer<typeof buttonElementSchema> {}

const inputElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.INPUT),
  inputType: createEnum(inputTypes, { enumName: "InputTypes" }),
  placeholderText: z.string(),
  customField: z
    .object({
      id: z.string(),
      fieldType: createEnum(CustomFieldType, { enumName: "CustomFieldType" }),
    })
    .optional(),
  optional: z.boolean().optional(),
  styleOverride: inputStyleOverrideSchema_.nullish(),
});
export const InputElementSchema: z.ZodType<InputElement> = inputElementSchema;
export interface InputElement extends z.infer<typeof inputElementSchema> {}

//redontIgnore
export const objectFitOptions = ["cover", "contain"] as const;
export type ObjectFit = (typeof objectFitOptions)[number];
export const ObjectFit = objectFitOptions;

const imageElementSchema_ = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.IMAGE),
  url: z.string(),
  altText: z.string().optional(),
  objectFit: createEnum(objectFitOptions, { enumName: "ObjectFit" }).optional(),
  // Explicit render size, each axis gated behind its own "use native"
  // toggle. An unset toggle means native, so an image authored before these
  // fields existed - and one where the merchant left both toggles on -
  // renders exactly as it always has: filling the available width at its
  // intrinsic height. A falsy px value is treated as native too, since the
  // inspector's number input bottoms out at 0.
  useNativeWidth: z.boolean().optional(),
  useNativeHeight: z.boolean().optional(),
  widthPx: z.number().min(0).optional(),
  heightPx: z.number().min(0).optional(),
});
export const imageElementSchema: z.ZodType<ImageElement> = imageElementSchema_;
export const ImageElementSchema: z.ZodType<ImageElement> = imageElementSchema_;
export interface ImageElement extends z.infer<typeof imageElementSchema_> {}

export enum ProductLayout {
  VERTICAL = "vertical",
  HORIZONTAL = "horizontal",
}

const productElementSchema_ = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.PRODUCT),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
  productImageUrl: z.string().optional(),
  productPrice: z.string().optional(),
  productCompareAtPrice: z.string().optional(),
  productLayout: createEnum(ProductLayout, { enumName: "ProductLayout" }),
  fontFamily: z.string().optional(),
  fontSize: z.number().optional(),
});
export const productElementSchema: z.ZodType<ProductElement> =
  productElementSchema_;
export const ProductElementSchema: z.ZodType<ProductElement> =
  productElementSchema_;
export interface ProductElement extends z.infer<typeof productElementSchema_> {}

export enum Alignment {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export const alignmentToFlexAlign: Record<Alignment, string> = {
  [Alignment.LEFT]: "flex-start",
  [Alignment.CENTER]: "center",
  [Alignment.RIGHT]: "flex-end",
};

const discountCodeElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.DISCOUNT),
  discountId: z.string().optional(),
  fontFamily: z.string(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
  fontSize: z.number(),
  textColor: z.string(),
  blockBackgroundColor: z.string(),
  /** @deprecated use baseElementSchema.backgroundColor instead */
  sectionColor: z.string(),
  /** @deprecated use baseElementSchema.padding instead */
  sectionPadding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  alignment: createEnum(Alignment, { enumName: "Alignment" }),
  borderRadius: z.number(),
});
export const DiscountCodeElementSchema: z.ZodType<DiscountCodeElement> =
  discountCodeElementSchema;
export interface DiscountCodeElement extends z.infer<
  typeof discountCodeElementSchema
> {}

const textToSubscribeButtonElementSchema = buttonElementSchema.extend({
  type: z.literal(MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON),
});
export const TextToSubscribeButtonElementSchema: z.ZodType<TextToSubscribeButtonElement> =
  textToSubscribeButtonElementSchema;
export interface TextToSubscribeButtonElement extends z.infer<
  typeof textToSubscribeButtonElementSchema
> {}

const textToSubscribeDescriptionElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION),
  fontFamily: z.string(),
  textColor: z.string(),
});
export const TextToSubscribeDescriptionElementSchema: z.ZodType<TextToSubscribeDescriptionElement> =
  textToSubscribeDescriptionElementSchema;
export interface TextToSubscribeDescriptionElement extends z.infer<
  typeof textToSubscribeDescriptionElementSchema
> {}

const ElementUnion = z.union([
  richTextElementSchema_,
  buttonElementSchema,
  inputElementSchema,
  imageElementSchema_,
  productElementSchema_,
  discountCodeElementSchema,
  textToSubscribeButtonElementSchema,
  textToSubscribeDescriptionElementSchema,
]);

const columnElementSchema = baseElementSchema.extend({
  type: z.literal(MarketingForgeElementType.COLUMN),
  elements: z.array(z.lazy(() => ElementUnion)),
  totalColumns: z.number(),
  columnWidths: z.array(z.number()).optional(),
});
export const ColumnElementSchema: z.ZodType<ColumnElement> =
  columnElementSchema;
export interface ColumnElement extends z.infer<typeof columnElementSchema> {}

export const containerDirections = ["vertical", "horizontal"] as const;
export type ContainerDirection = (typeof containerDirections)[number];

export const containerJustifications = ["start", "center", "end"] as const;
export type ContainerJustification = (typeof containerJustifications)[number];

const containerJustificationToFlexJustify: Record<
  ContainerJustification,
  "flex-start" | "center" | "flex-end"
> = { start: "flex-start", center: "center", end: "flex-end" };

export interface ContainerLayout {
  direction: "row" | "column";
  justifyContent: "flex-start" | "center" | "flex-end";
}

/**
 * Resolves a container's flex layout.
 *
 * Justification applies to vertical containers only. A horizontal container's
 * children fill the row, so there is no free space for `justify-content` to
 * distribute and the setting is a no-op - keyed off the authored direction,
 * so a horizontal container stays a no-op on mobile too, where it collapses
 * to a column.
 */
export function containerLayout(
  element: Pick<ContainerElement, "direction" | "justify">,
  isMobile: boolean,
): ContainerLayout {
  const isVertical = element.direction === "vertical";
  const justify = isVertical ? (element.justify ?? "start") : "start";
  return {
    // Containers collapse to a column on mobile regardless of direction.
    direction: isMobile || isVertical ? "column" : "row",
    justifyContent: containerJustificationToFlexJustify[justify],
  };
}

type ContainerElement = BaseElement & {
  type: MarketingForgeElementType.CONTAINER;
  direction: ContainerDirection;
  justify?: ContainerJustification;
  gapPx?: number;
  elements: (
    | RichTextElement
    | ButtonElement
    | InputElement
    | ImageElement
    | ProductElement
    | ColumnElement
    | DiscountCodeElement
    | TextToSubscribeButtonElement
    | TextToSubscribeDescriptionElement
    | ContainerElement
  )[];
};

const containerElementSchema: z.ZodType<ContainerElement> = z.lazy(() =>
  baseElementSchema.extend({
    type: z.literal(MarketingForgeElementType.CONTAINER),
    direction: createEnum(containerDirections, {
      enumName: "ContainerDirection",
    }),
    justify: createEnum(containerJustifications, {
      enumName: "ContainerJustification",
    }).optional(),
    gapPx: z.number().optional(),
    elements: z.array(
      z.union([
        richTextElementSchema_,
        buttonElementSchema,
        inputElementSchema,
        imageElementSchema_,
        productElementSchema_,
        columnElementSchema,
        discountCodeElementSchema,
        textToSubscribeButtonElementSchema,
        textToSubscribeDescriptionElementSchema,
        containerElementSchema,
      ]),
    ),
  }),
);

export const ContainerElementSchema: z.ZodType<ContainerElement> =
  containerElementSchema;
export type { ContainerElement };

const FormElementUnion = z.union([
  richTextElementSchema_,
  buttonElementSchema,
  inputElementSchema,
  imageElementSchema_,
  productElementSchema_,
  columnElementSchema,
  discountCodeElementSchema,
  textToSubscribeButtonElementSchema,
  textToSubscribeDescriptionElementSchema,
  containerElementSchema,
]);

export type FormElement = z.infer<typeof FormElementUnion>;
